import {
  ICursorPaginatedData,
  ICursorPaginatedInput,
  loadAllCursorPaginatedChunks,
} from 'modules/common/utils/loadCursorPaginatedData';
import { useState, useCallback } from 'react';

interface IUseChunkedLoaderOptions<T> {
  query: (input: ICursorPaginatedInput) => Promise<ICursorPaginatedData<T>>;
  afterLoad?: (data: T[]) => Promise<void>;
  onError?: (error: Error) => void;
  onEmpty?: () => void;
}

export function useChunkedLoader<T>({
  query,
  afterLoad,
  onError,
  onEmpty,
}: IUseChunkedLoaderOptions<T>) {
  const [data, setData] = useState<T[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const queryChunk = useCallback(
    async (input: ICursorPaginatedInput): Promise<ICursorPaginatedData<T>> => {
      const result = await query(input);
      if (!result?.meta) {
        throw new Error('Failed to load chunk meta data');
      }
      return result;
    },
    [query]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  const loadData = useCallback(async () => {
    reset();
    try {
      setIsLoading(true);
      setError(null);
      const mergedChunkData = await loadAllCursorPaginatedChunks(queryChunk);
      await afterLoad?.(mergedChunkData);
      setData(mergedChunkData);
      if (mergedChunkData.length === 0) {
        onEmpty?.();
      }
    } catch (err) {
      const caughtError = err as Error;
      setError(caughtError);
      onError?.(caughtError);
    } finally {
      setIsLoading(false);
    }
  }, [queryChunk, afterLoad, onError, onEmpty, reset]);

  return {
    data,
    isLoading,
    error,
    load: loadData,
    reset,
  };
}
